import React, { useEffect, useRef } from 'react';
import { useTheme } from 'src/context/ThemeContext';

/**
 * PlatformWaffle
 *
 * Thin React wrapper around the shared <platform-waffle> app-launcher web
 * component served by idb-next at /platform/waffle.js. Replaces the previously
 * self-hosted WaffleMenu in the header per the platform integration guide.
 *
 * How it resolves its backend:
 *   - Production: same-origin; ingress routes /platform/* to idb-next.
 *   - Local dev:  src/setupProxy.js forwards /platform/* to the idb dev host.
 *
 * Auth: the widget is handed the idb-next `id_token` via the `token` attribute
 * and sends it to /platform/api/my-apps, where idb-next validates it against its
 * JWKS and reads the `t_slug` / `access[]` claims. This id_token is surfaced by
 * phishing_backend's OIDC token-exchange (stored as `idbIdToken`) — it is NOT
 * the portal's own `authToken`. It therefore only exists for SSO/OIDC logins; on
 * username/password login there is no idb-next identity, so the widget will emit
 * `platform-waffle-auth-error` (handled below).
 */
const WAFFLE_SRC = '/platform/waffle.js';

function loadWaffleScript() {
	if (document.querySelector(`script[src="${WAFFLE_SRC}"]`)) return;
	const script = document.createElement('script');
	script.src = WAFFLE_SRC;
	script.async = true;
	document.head.appendChild(script);
}

function PlatformWaffle() {
	const ref = useRef(null);
	const { theme } = useTheme();

	// Only idb-next (SSO/OIDC) sessions carry an id_token. Username/password users
	// have no idb-next identity, so the launcher could only ever show an empty,
	// 401-ing menu — hide the widget entirely for them.
	const idbToken = localStorage.getItem('idbIdToken') || '';

	// Inject the widget script once (only when we actually have a token to use).
	useEffect(() => {
		if (!idbToken) return;
		loadWaffleScript();
	}, [idbToken]);

	// Keep the token attribute in sync with the current session.
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		el.setAttribute('token', idbToken);
	}, [idbToken]);

	// React to the widget rejecting the token (401 from idb-next).
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const handleAuthError = () => {
			// The widget's token was rejected. There is no idb-next token-refresh
			// flow wired into this portal yet, so we surface it for diagnostics
			// rather than silently swallowing. Hook a re-auth here if/when idb-next
			// issues this app's session tokens.
			// eslint-disable-next-line no-console
			console.warn('[platform-waffle] auth error: token rejected by idb-next');
		};
		el.addEventListener('platform-waffle-auth-error', handleAuthError);
		return () => el.removeEventListener('platform-waffle-auth-error', handleAuthError);
	}, []);

	// Non-idb-next users: no launcher at all.
	if (!idbToken) return null;

	return <platform-waffle ref={ref} theme={theme === 'dark' ? 'dark' : 'light'} />;
}

export default PlatformWaffle;
